/**
 * Firebase Web SDK init — used only for Phone Auth (OTP).
 * Google sign-in remains on the existing Emergent flow.
 *
 * Safe to import even when env vars are missing: getFirebaseAuth() will
 * return null, and callers can fall back to "phone sign-in unavailable" UI.
 */
import { initializeApp, getApps } from "firebase/app";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let _authInstance = null;

export function isPhoneAuthConfigured() {
    return !!(config.apiKey && config.authDomain && config.projectId && config.appId);
}

export function getFirebaseAuth() {
    if (!isPhoneAuthConfigured()) return null;
    if (_authInstance) return _authInstance;
    const app = getApps()[0] || initializeApp(config);
    _authInstance = getAuth(app);
    // English UI for SMS prompts
    _authInstance.languageCode = "en";
    return _authInstance;
}

/**
 * Build (or reuse) an invisible reCAPTCHA verifier bound to a DOM container.
 * Caller MUST render <div id={containerId} /> in their component tree.
 */
export function getRecaptchaVerifier(containerId) {
    const auth = getFirebaseAuth();
    if (!auth) return null;
    // Reuse one verifier per container — re-creating throws "already rendered"
    if (window.__jnsRecaptcha?.[containerId]) {
        return window.__jnsRecaptcha[containerId];
    }
    const verifier = new RecaptchaVerifier(auth, containerId, { size: "invisible" });
    window.__jnsRecaptcha = window.__jnsRecaptcha || {};
    window.__jnsRecaptcha[containerId] = verifier;
    return verifier;
}

/**
 * Clear (force a fresh challenge on next send-OTP). Call this when the modal
 * closes so retries don't fail with stale token errors.
 */
export function resetRecaptcha(containerId) {
    try {
        const v = window.__jnsRecaptcha?.[containerId];
        if (v && typeof v.clear === "function") v.clear();
    } catch { /* noop */ }
    if (window.__jnsRecaptcha) delete window.__jnsRecaptcha[containerId];
}

export { signInWithPhoneNumber };
